/**
 * Профиль на устройстве: типы, умолчания и чистые действия над ними.
 *
 * Лежит в `shared/`, потому что читателей двое — web и натив, — а копия у
 * каждого уже начала расходиться: в вебе к профилю добавились поля окна
 * начисления и подписки, в нативе их нет. Пока расхождение осмысленное (в
 * нативе нет ни покупок, ни рекламы), но ровно так же в своё время разошлись
 * строки интерфейса и список карт с иллюстрациями, и обе копии пришлось
 * сводить задним числом.
 *
 * **Здесь нет самого хранилища, и это главное.** Читают и пишут профиль
 * по-разному: в вебе `localStorage`, в нативе `expo-sqlite/kv-store` (он
 * синхронный, и на этом держится инициализация стора — профиль читается до
 * первого кадра). Общее — форма профиля и то, что с ней делают.
 *
 * Сервером НЕ импортируется: это состояние устройства, а не пользователя.
 */
import type { Lang } from './cards.ts'
import type { SpreadId } from './spreads.ts'
import type { Place } from './places.ts'
import type { Gender } from './taxonomy.ts'

export interface DrawnCard {
  id: number
  reversed: boolean
}

/** Совпадают ли два набора вытянутых карт: те же карты, в том же порядке. */
export function sameDraw(a: DrawnCard[], b: DrawnCard[]): boolean {
  return a.length === b.length && a.every((c, i) => c.id === b[i].id && c.reversed === b[i].reversed)
}

/** Реплика в разговоре о раскладе: `p` — таролог, `u` — сам человек. */
export interface AskLine {
  who: 'p' | 'u'
  text: string
}

export interface ReadingRecord {
  /** Локальный id: `${ts}-${spread}` — совпадает с id на бэкенде. */
  id: string
  ts: number
  spread: SpreadId
  cards: DrawnCard[]
  lang: Lang
  /** Текст AI-толкования: общий вывод + по одной строке на позицию. */
  overall: string
  perCard: string[]
  /**
   * Момент, с которого расклад можно показать (отложенная выдача); 0 — сразу.
   * Пока он не наступил, сервер не отдаёт текста вовсе, и `overall` пуст.
   */
  readyAt?: number
  /**
   * Тема, под которую расклад сделан. Пусто — «ни о чём конкретно».
   *
   * Нужна не для показа, а для правила «один расклад в день»: оно считает
   * тему частью ключа, и без неё клиент подсунул бы вчерашний расклад про
   * любовь человеку, который сегодня спрашивает про работу.
   */
  theme?: string
  /** Вопросы по этому раскладу и ответы на них. Приходят с сервера. */
  chat?: AskLine[]
  /**
   * Сколько вопросов осталось. Считает сервер — на нём же и проверяется;
   * здесь число только для того, чтобы не показывать поле, когда спрашивать
   * уже нечего.
   */
  turnsLeft?: number
}

export interface BirthInput {
  year: number
  month: number
  day: number
  /** null — время не введено. */
  hour: number | null
  minute: number | null
  /** Место рождения с координатами и часовым поясом; null — ещё не выбрано. */
  place: Place | null
  /**
   * Пользователь явно ответил, что времени не знает. Отличается от «время
   * ещё не введено»: во втором случае поле надо показать пустым и включённым,
   * а не решать за него, что времени нет.
   */
  timeUnknown: boolean
}

export interface Profile {
  lang: Lang
  /** Имя для приветствия. В Telegram берётся из Telegram, в PWA — с сервера
   *  (из Google-аккаунта или поля при регистрации). Пустое → «Гость». */
  name: string
  /** Согласие на уведомления (карта дня, акции). */
  notify: boolean
  onboarded: boolean
  /** Данные рождения для натальной карты. */
  birth: BirthInput | null
  /** Дата рождения партнёра для совместимости (время и место не нужны). */
  partner: { year: number; month: number; day: number } | null
  goals: string[]
  path: string
  zodiac: string
  /**
   * Пол — уходит в промпт, чтобы толкование говорило с этим человеком, а не
   * с кем-то вообще. `u` («не указывать») — полноценный ответ: на нём модель
   * обходит род стороной, как делала до появления этого поля.
   */
  gender: Gender
  /**
   * Человек выбрал язык сам — на экране входа или в профиле.
   *
   * Живёт только на устройстве и на сервер не уезжает: это не свойство
   * человека, а признак «его выбор уже есть, перебивать нечем». Без флага
   * ответ сервера сносил только что сделанный выбор, потому что до онбординга
   * серверный язык считался вернее любого.
   */
  langChosen: boolean
  /**
   * Тема ближайшего чтения — id одной из целей или null.
   *
   * Отдельно от `goals`: цели человек выбрал однажды в онбординге, а тему —
   * прямо сейчас и для этого гадания. В промпте она и весит больше.
   * Живёт только на устройстве: это состояние минуты, а не профиль.
   */
  theme: string | null
  /** Открытая карта дня: ключ дня → индекс карты. */
  dayFlipped: Record<string, boolean>
  favorites: number[]
  history: ReadingRecord[]
  stars: number
  streak: number
  lastSeenDay: string
  /**
   * Окно повышенного начисления для новичка: до какого момента оно действует
   * (unix мс) и во сколько раз больше начисляют. Считает и закрывает окно
   * сервер — здесь только для обратного отсчёта на экране.
   */
  bonusUntil?: number
  bonusMultiplier?: number
  /**
   * До какого момента действует льготная цена подписки (unix мс); 0 — уже нет.
   *
   * Считает сервер от времени регистрации, здесь — только обратный отсчёт на
   * полосе и в окне. Своей арифметики быть не должно: на двух устройствах она
   * дала бы два разных «осталось», а цену всё равно решает сервер.
   */
  introUntil?: number
  /**
   * Действует ли подписка. Считает сервер — здесь только чтобы не показывать
   * подписчику рекламу и не обещать ему то, за что он уже заплатил.
   */
  subscribed?: boolean
  /**
   * Получали ли мы хоть раз состояние с сервера. До этого свои скалярные поля
   * (цели, избранное, данные рождения) отправлять нельзя: пустой профиль
   * нового устройства затёр бы накопленное.
   */
  synced: boolean
}

export const EMPTY_PROFILE: Profile = {
  // Азербайджанский — язык по умолчанию: рынок Азербайджана, и первое, что
  // видит новый человек, должно быть на нём. Выбор пользователя это не трогает.
  lang: 'az',
  name: '',
  notify: false,
  onboarded: false,
  birth: null,
  partner: null,
  goals: [],
  path: 'novice',
  zodiac: 'leo',
  gender: 'u',
  langChosen: false,
  theme: null,
  dayFlipped: {},
  favorites: [],
  history: [],
  stars: 0,
  streak: 0,
  lastSeenDay: '',
  synced: false,
}

/** Обновляет серию визитов: +1 за визит в следующий день, сброс при пропуске. */
export function bumpStreak(p: Profile, today: string): Profile {
  if (p.lastSeenDay === today) return p
  const yesterday = new Date(Date.now() - 86400_000)
  const yKey = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(
    yesterday.getDate(),
  ).padStart(2, '0')}`
  return {
    ...p,
    streak: p.lastSeenDay === yKey ? p.streak + 1 : 1,
    lastSeenDay: today,
  }
}
